// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { ObjectTypeV1 as ObjectType, TaxonomyOutput, AdminPermissionsEnum } from 'thehive-sdk';
import { openMessage, handleNotificationError } from '@Helpers/notification';
import { DisplayBadge } from '@Atoms/Display';
import { TaxonomyImport, TaxonomyPreview } from '@Organisms/Taxonomy';
import { ICONS } from '@Constants/iconsApp';
import { useLocalMessage } from '@Helpers/locale';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { useCurrentUser } from '@Queries/userQueries';
import usePermissions from '@Hooks/usePermissions';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';

import './TaxonomiesList.scss';

const TaxonomiesList = (): ReactElement => {
    const i = useLocalMessage();

    const config = useConfigList<TaxonomyOutput>({
        selector: {
            head: [{ name: 'listTaxonomy' }],
            extraData: ['enabled'],
            name: 'get-all-taxonomies',
            sort: ['+namespace'],
        },
        controller: client.TaxonomyController,
        storageId: StorageFilterEnum.TAXONOMY,
        objectType: ObjectType.TAXONOMY,
    });

    const {
        listRefetch,
        countRefetch,
        dataSource = [],
        columnsRenderer,
        actionListRenderer,
        filterManager,
        status = 'loading',
        paginationManager,
        table,
        storagePageManager,
        storageViewManager,
    } = config;

    const actionList = actionListRenderer((selectedItems, onSuccess) => {
        const firstTaxo = selectedItems.find(Boolean);

        return {
            activate: {
                query: (): Promise<void> =>
                    client.TaxonomyController.activate(firstTaxo?._id || '')
                        .then(() => {
                            openMessage('success', i('taxonomy.list.actions.activate.success'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                label: i('taxonomy.list.actions.activate.label'),
                icon: <ICONS.CheckCircleTwoTone twoToneColor="#52c41a" />,
                hidden: firstTaxo?.extraData?.enabled,
                scope: 'single',
            },
            deactivate: {
                query: (): Promise<void> =>
                    client.TaxonomyController.deactivate(firstTaxo?._id || '')
                        .then(() => {
                            openMessage('success', i('taxonomy.list.actions.deactivate.success'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                label: i('taxonomy.list.actions.deactivate.label'),
                icon: <ICONS.CloseCircleTwoTone twoToneColor="#e60000" />,
                hidden: !firstTaxo?.extraData?.enabled,
                scope: 'single',
            },
            delete: {
                query: (): Promise<void> =>
                    client.TaxonomyController.delete(firstTaxo?._id || '')
                        .then(() => {
                            openMessage('success', i('taxonomy.list.actions.delete.success'));
                            onSuccess(true);
                        })
                        .catch(handleNotificationError),
                label: i('taxonomy.list.actions.delete.label'),
                icon: <ICONS.DeleteIcon />,
                scope: 'single',
            },
        };
    });

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = usePermissions({
        permissionsNeeded: [AdminPermissionsEnum.MANAGE_TAXONOMY],
        permissionsList: permissions,
    });

    const columns = columnsRenderer([
        {
            className: 'taxonomies-list__enabled',
            render: ({ extraData }, { custom }): ReactElement => (
                <DisplayBadge
                    status={!!extraData?.enabled ? 'success' : 'error'}
                    onClick={(): void => custom({ field: 'enabled', operator: !!extraData?.enabled + '' })}
                />
            ),
        },
        {
            title: i('taxonomy.list.columns.namespace'),
            className: 'taxonomies-list__namespace',
            render: (taxonomy): ReactElement => <TaxonomyPreview taxonomy={taxonomy} />,
        },
        {
            title: i('taxonomy.list.columns.description'),
            className: 'taxonomies-list__description',
            render: ({ description }): ReactElement => <div>{description}</div>,
        },
        {
            title: i('taxonomy.list.columns.version'),
            className: 'taxonomies-list__version',
            render: ({ version }): ReactElement => <div>{version}</div>,
        },
        {
            title: i('taxonomy.list.columns.tags'),
            className: 'taxonomies-list__tags',
            render: ({ tags = [] }): ReactElement => <div>{tags.length}</div>,
        },
        {
            className: 'taxonomies-list__actions',
            render: (taxonomy): ReactElement | null =>
                hasPermissions ? (
                    <ActionsList
                        actionList={actionList([taxonomy], (count) => {
                            listRefetch();
                            if (count) {
                                countRefetch();
                            }
                        })}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    const { data: describe } = useListAllFields();

    return (
        <THList>
            <THList.Header>
                <THList.Slot name="MANAGE">
                    {hasPermissions && (
                        <TaxonomyImport
                            onSubmit={(formData): Promise<void> =>
                                client.TaxonomyController.import(formData)
                                    .then(() => {
                                        listRefetch();
                                        countRefetch();
                                    })
                                    .catch(handleNotificationError)
                            }
                        />
                    )}
                    {storagePageManager && storageViewManager ? (
                        <ManageViewList
                            pageManager={storagePageManager}
                            viewManager={storageViewManager}
                            beforeCreate={(content: any): any => {
                                filterManager.applyFilters();
                                return { ...content, filters: filterManager.tempFilters };
                            }}
                        />
                    ) : null}
                </THList.Slot>
                <THList.Slot name="NAVIGATION">
                    <ManageSwitch
                        label={i('common.filters')}
                        value={filterManager.editFilters}
                        onChange={(editFilters): void => {
                            filterManager.setEditFilters(editFilters);
                        }}
                        icon={<ICONS.FilterOutlined />}
                    />
                </THList.Slot>
                <THList.Slot name="CONTENT">
                    {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                        <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.TAXONOMY]}>
                            <Footer filterConfig={filterManager} />
                        </THFilter>
                    )}
                </THList.Slot>
            </THList.Header>

            <THList.Content
                dataSource={dataSource}
                columns={columns}
                rowKey="_id"
                emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('taxonomy.none')} />}
            />

            <THList.Footer>
                <THList.Slot name="NAVIGATION">
                    <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                    <PaginationSize paginationConfig={paginationManager} />
                </THList.Slot>
            </THList.Footer>
        </THList>
    );
};

const path = RouteBuilder.compute('administration.taxonomies');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.taxonomies',
        icon: <ICONS.TagsOutlined />,
    },
    breadcrumb: { label: 'breadcrumb.global.taxonomies' },
    content: <TaxonomiesList />,
    space: ['admin'],
    permissionNeeded: [AdminPermissionsEnum.MANAGE_TAXONOMY],
    exact: true,
};
